import type { Money } from '@dastarhan/contracts';

/**
 * Пересчёт склада.
 *
 * Кладовщик обходит полки со списком и вписывает, сколько нашёл.
 * Программа сравнивает с учётом и показывает расхождение — но само
 * расхождение ещё ничего не значит: важно, ПОЧЕМУ оно возникло.
 *
 * Недостача от боя, от воровства и от неверной техкарты лечатся
 * по-разному, и без причины владелец видит только цифру, с которой
 * нечего делать.
 */

export type InvLine = {
  productId: string;
  name: string;
  unit: string;
  /** сколько должно быть по учёту на момент снимка */
  expected: number;
  /** сколько нашли; null — ещё не считали */
  actual: number | null;
  /** себестоимость единицы в тиынах */
  cost: Money;
  reason?: InvReason | null;
  note?: string | null;
};

/** Почему факт разошёлся с учётом. Список короткий: длинный не читают. */
export const INV_REASONS = [
  {
    key: 'BREAKAGE' as const,
    label: 'Бой и порча',
    hint: 'разбили, испортилось, просрочка',
  },
  {
    key: 'RECIPE' as const,
    label: 'Ошибка техкарты',
    hint: 'в блюде уходит больше или меньше, чем записано',
  },
  {
    key: 'RECEIPT' as const,
    label: 'Ошибка приёмки',
    hint: 'привезли не столько, сколько записали',
  },
  {
    key: 'STAFF' as const,
    label: 'Питание персонала',
    hint: 'съели свои, не провели',
  },
  {
    key: 'THEFT' as const,
    label: 'Пропажа',
    hint: 'объяснения нет — это разговор с людьми',
  },
  {
    key: 'OTHER' as const,
    label: 'Другое',
    hint: 'опишите словами — это попадёт в акт',
  },
] as const;

export type InvReason = typeof INV_REASONS[number]['key'];

/** Причины, для которых объяснение словами обязательно. */
export function invNeedsNote(r: InvReason): boolean {
  return r === 'OTHER' || r === 'THEFT';
}

export type InvTotals = {
  /** сколько позиций пересчитано из скольких */
  counted: number;
  total: number;
  /** нашли больше, чем ждали, в единицах */
  surplusQty: number;
  /** нашли меньше, в единицах (положительное число) */
  shortageQty: number;
  /** деньгами: излишек минус недостача */
  diffMoney: Money;
  /** только недостача деньгами — её списывают */
  shortageMoney: Money;
  /** сколько строк с расхождением ждут причины */
  needReason: number;
};

/**
 * Итоги пересчёта.
 *
 * Считаем излишек и недостачу ОТДЕЛЬНО, а не одной суммой. Пять кило
 * лишней картошки и пять кило пропавшего мяса в сумме дают ноль —
 * и владелец решил бы, что всё в порядке.
 */
export function invTotals(lines: InvLine[]): InvTotals {
  let counted = 0;
  let surplusQty = 0;
  let shortageQty = 0;
  let surplusMoney = 0;
  let shortageMoney = 0;
  let needReason = 0;

  for (const l of lines) {
    if (l.actual === null) continue;
    counted += 1;

    const diff = l.actual - l.expected;
    if (diff === 0) continue;

    // Округляем деньги один раз, на строке: сумма округлений
    // по сотне позиций даёт заметную ошибку
    const money = Math.round(Math.abs(diff) * l.cost);
    if (diff > 0) {
      surplusQty += diff;
      surplusMoney += money;
    } else {
      shortageQty += -diff;
      shortageMoney += money;
    }

    if (!l.reason) needReason += 1;
  }

  return {
    counted,
    total: lines.length,
    surplusQty: round3(surplusQty),
    shortageQty: round3(shortageQty),
    diffMoney: surplusMoney - shortageMoney,
    shortageMoney,
    needReason,
  };
}

/** Граммы и миллилитры считаем до тысячных: 0,125 кг — обычное дело. */
function round3(v: number): number {
  return Math.round(v * 1000) / 1000;
}

/**
 * Можно ли проводить.
 *
 * Незаполненные строки — не ошибка: склад считают частями, сначала
 * бар, потом кухню. Но расхождение без причины проводить нельзя:
 * через месяц никто не вспомнит, откуда взялась недостача на сорок
 * тысяч, и она превратится в «так было».
 */
export function invCanPost(lines: InvLine[]): { ok: true } | { ok: false; reason: string } {
  const t = invTotals(lines);
  if (t.counted === 0) {
    return { ok: false, reason: 'Ни одна позиция не пересчитана' };
  }
  if (t.needReason > 0) {
    return {
      ok: false,
      reason: `Расхождений без причины: ${t.needReason}. Укажите, откуда разница`,
    };
  }
  const noNote = lines.find(
    (l) => l.actual !== null && l.actual !== l.expected
      && l.reason && invNeedsNote(l.reason) && !l.note?.trim(),
  );
  if (noNote) {
    return { ok: false, reason: `«${noNote.name}»: опишите причину словами` };
  }
  return { ok: true };
}

/**
 * Номер документа: INV-20260804-A7K2.
 *
 * Дата в номере читается человеком без базы, а хвост из четырёх
 * знаков разводит два пересчёта одного дня — бар утром и кухню вечером.
 */
export function invCode(at: Date, salt = Math.random()): string {
  const d = [
    at.getFullYear(),
    String(at.getMonth() + 1).padStart(2, '0'),
    String(at.getDate()).padStart(2, '0'),
  ].join('');
  const tail = Math.floor(salt * 36 ** 4).toString(36).toUpperCase().padStart(4, '0');
  return `INV-${d}-${tail}`;
}
